import { useRef, useState } from "react";
import { tokens } from "@core/theme/tokens";
import type { BilingualText, Template } from "@core/types";
import {
  Badge,
  DialogHeader,
  EditorFooter,
  EditorModal,
  type EditorModalTarget,
  InlineNote,
  LabeledField as Field,
  LanguageSwitch,
  inputStyle,
  RowIconAction,
  useApiFieldErrors,
  useEditorForm,
  type ContentLanguage,
} from "@portal/common";
import type { TemplateEditInput } from "./api";
import { useTemplateMutations } from "./messageTemplateQueries";
import {
  isEmptyRichText,
  normalizeRichText,
  RichTextEditor,
  type RichTextEditorHandle,
} from "./RichTextEditor";
import {
  BODY_REQUIRED_MESSAGE,
  parameterMissingMessage,
  SUBJECT_REQUIRED_MESSAGE,
  templateFormSchema,
  type TemplateFormValues,
} from "./messageTemplateSchemas";
import { TemplatePreviewDialog } from "./TemplatePreview";

// Los dos únicos campos de este formulario que la API puede identificar por
// nombre (`Problem.errors[].field`, `fieldRejection` de
// `message-template.service.ts`): el asunto solo en plantillas de correo y el
// cuerpo con forma bilingüe inválida. `subjectEn`/`bodyEn` no son un campo
// que el servidor rechace por nombre propio (RN-24: el inglés respalda al
// español, nunca al revés) — el pre-chequeo de parámetro faltante en inglés
// sí puede marcar `bodyEn` (ver `findMissingParameterClient` más abajo), así
// que también se declara acá.
const TEMPLATE_EDITOR_FORM_FIELDS: readonly (keyof TemplateFormValues)[] = [
  "subjectEs",
  "bodyEs",
  "bodyEn",
];

// Espejo de `findMissingParameter` (`api/src/management/message-templates/
// message-template.service.ts`): el mismo criterio (español siempre, inglés
// solo si viene) sobre el cuerpo que se está por enviar — antes de este fix
// solo el servidor lo comprobaba (SPEC-218 §4.2, criterio 4), así que borrar
// `{code}` del correo del token de acceso pasaba el formulario sin aviso y
// recién se enteraba al ver el 400 `PARAMETER_MISSING`.
function findMissingParameterClient(
  parameters: readonly string[],
  body: BilingualText,
): { readonly parameter: string; readonly language: "es" | "en" } | null {
  const languages: readonly ("es" | "en")[] =
    body.en === undefined ? ["es"] : ["es", "en"];
  for (const language of languages) {
    const text = language === "es" ? body.es : (body.en ?? "");
    for (const parameter of parameters) {
      if (!text.includes(`{${parameter}}`)) return { parameter, language };
    }
  }
  return null;
}

// PA-036-D (resolución humana): las tres plantillas del portal y del
// validador que SPEC-218 §4.3 exime del bilingüismo. El dominio
// (`api/src/management/message-templates/message-template.service.ts`) es
// la fuente de verdad — este conjunto solo gobierna qué muestra el editor,
// nunca lo que el servidor valida.
const SPANISH_ONLY_TEMPLATE_CODES: ReadonlySet<string> = new Set([
  "password_recovery",
  "offer_request",
  "appointment_booked",
]);

const CHANNEL_LABEL: Record<Template["channel"], string> = {
  email: "Correo",
  push: "Push",
};

function textOf(value: BilingualText | undefined, language: ContentLanguage) {
  if (!value) return "";
  return language === "es" ? value.es : (value.en ?? "");
}

function defaultsFor(template: Template): TemplateFormValues {
  return {
    subjectEs: textOf(template.subject, "es"),
    subjectEn: textOf(template.subject, "en"),
    bodyEs: textOf(template.body, "es"),
    bodyEn: textOf(template.body, "en"),
    active: template.active,
  };
}

interface TemplateEditorProps {
  template: Template;
  canEdit: boolean;
  submitting: boolean;
  saveError: string | undefined;
  onSave: (input: TemplateEditInput) => void;
  onDeactivate: () => void;
  onCancel: () => void;
  // Revisión en vivo 2026-09-22, puntos #3/#4A: el editor ES el diálogo. La
  // pantalla ya no lo envuelve en un `Modal`, le pasa acá el destino.
  modal: EditorModalTarget;
  // Mismo patrón que `CatalogEntryForm` (caso real
  // f4575316-5c75-4c27-83d0-340db0aafd9f): el 400 de guardado llega al
  // `onError` de la mutación, que vive en `MessageTemplatesScreen` (dueño del
  // `useToast`), no acá. Cuando la API identificó el campo
  // (`problem.errors[].field`, `INVALID_REQUEST` de
  // `MessageTemplateService.update`), este editor lo asocia a su input con
  // `applyApiFieldErrors`; si no pudo, se lo devuelve al padre por
  // `onUnhandledSubmitError` para que muestre el toast genérico de siempre.
  submitError?: unknown;
  onUnhandledSubmitError?: (error: unknown) => void;
}

export function TemplateEditor({
  template,
  canEdit,
  submitting,
  saveError,
  onSave,
  onDeactivate,
  onCancel,
  modal,
  submitError,
  onUnhandledSubmitError,
}: TemplateEditorProps) {
  const bilingual = !SPANISH_ONLY_TEMPLATE_CODES.has(template.code);
  const isEmail = template.channel === "email";
  const [language, setLanguage] = useState<ContentLanguage>("es");
  const { register, watch, setValue, errors, setError, handleSubmit } =
    useEditorForm<TemplateFormValues>(defaultsFor(template));

  // `markHandledOnApply`: ya se mostró bajo su campo, así que sin esto el
  // fallback global de `main.tsx` (`isErrorHandled`, `queryErrorFallback.ts`)
  // mostraría además el toast genérico un macrotask después.
  useApiFieldErrors(
    submitError,
    setError as (
      field: string,
      error: { type: string; message: string },
    ) => void,
    TEMPLATE_EDITOR_FORM_FIELDS,
    onUnhandledSubmitError,
    true,
  );
  const bodyEs = watch("bodyEs");
  const bodyEn = watch("bodyEn");
  const subjectEs = watch("subjectEs");
  const subjectEn = watch("subjectEn");
  const bodyEditorRef = useRef<RichTextEditorHandle>(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [testSent, setTestSent] = useState<string | undefined>(undefined);
  const mutations = useTemplateMutations();

  const activeBody = language === "es" ? bodyEs : bodyEn;

  const buildDraft = () => ({
    ...(isEmail
      ? {
          subject: {
            es: subjectEs,
            ...(subjectEn ? { en: subjectEn } : {}),
          },
        }
      : {}),
    body: {
      es: normalizeRichText(bodyEs),
      ...(bodyEn ? { en: normalizeRichText(bodyEn) } : {}),
    },
  });

  const submit = (values: TemplateFormValues) => {
    if (isEmptyRichText(values.bodyEs)) {
      setError("bodyEs", { message: BODY_REQUIRED_MESSAGE });
      return;
    }
    const parsed = templateFormSchema.safeParse(values);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        setError(issue.path[0] as keyof TemplateFormValues, {
          message: issue.message,
        });
      }
      return;
    }
    // SPEC-218 §4: el asunto solo aplica a plantillas de correo, pero en una
    // sí es obligatorio (`isBilingual` del servidor exige `es` no vacío) —
    // `templateFormSchema` no lo sabe (no conoce el canal), así que se
    // revisa acá, igual que el cuerpo vacío arriba.
    if (isEmail && parsed.data.subjectEs.trim() === "") {
      setError("subjectEs", { message: SUBJECT_REQUIRED_MESSAGE });
      return;
    }
    const body: BilingualText = {
      es: normalizeRichText(parsed.data.bodyEs),
      ...(parsed.data.bodyEn
        ? { en: normalizeRichText(parsed.data.bodyEn) }
        : {}),
    };
    // SPEC-218 §4.2, criterio 4: cada parámetro obligatorio debe seguir en
    // el cuerpo de cada idioma presente — antes solo lo comprobaba el
    // servidor (`findMissingParameter`); acá se replica para no dejar que un
    // 400 `PARAMETER_MISSING` sea la primera noticia.
    const missingParameter = findMissingParameterClient(
      template.parameters,
      body,
    );
    if (missingParameter !== null) {
      setError(missingParameter.language === "es" ? "bodyEs" : "bodyEn", {
        message: parameterMissingMessage(missingParameter.parameter),
      });
      return;
    }
    const subject: BilingualText | undefined = isEmail
      ? {
          es: parsed.data.subjectEs,
          ...(parsed.data.subjectEn ? { en: parsed.data.subjectEn } : {}),
        }
      : undefined;
    onSave({
      ...(subject ? { subject } : {}),
      body,
      active: parsed.data.active,
    });
  };

  const insertParameter = (parameter: string) => {
    bodyEditorRef.current?.insertText(`{${parameter}}`);
  };

  const openPreview = () => {
    setPreviewOpen(true);
    mutations.preview.mutate({ code: template.code, draft: buildDraft() });
  };

  const sendTest = () => {
    setTestSent(undefined);
    mutations.sendTest.mutate(
      { code: template.code, draft: buildDraft() },
      {
        // SPEC-218 §10: "Se envió una prueba a tu correo" — literal, sin
        // exponer el correo real crudo del `problem`/`detail` (SPEC-201
        // §4.4); `maskedEmail` ya viene enmascarado del servidor.
        onSuccess: (result) => setTestSent(result.maskedEmail),
      },
    );
  };

  return (
    <>
      <EditorModal
        modal={modal}
        onSubmit={handleSubmit(submit)}
        header={
          /* No se reutiliza `EditorHeader` (siempre muestra `LanguageSwitch`):
            PA-036-D exige que las tres plantillas solo en español no ofrezcan
            ningún conmutador de idioma, ni siquiera deshabilitado — mismo
            marcado y valores visuales, solo condicional en `bilingual`. El
            `padding` y el divisor que este bloque dibujaba a mano los pone
            ahora la región `header` del `Modal`, igual que en `EditorHeader`.
            Punto #21 (revisión en vivo 2026-09-22): eyebrow + título salen de
            `DialogHeader`, el encabezado común; el DOM no cambió. */
          <DialogHeader
            eyebrow="Editar plantilla"
            title={template.name}
            aside={
              <>
                {bilingual ? (
                  <LanguageSwitch value={language} onChange={setLanguage} />
                ) : null}
                {template.isCritical ? (
                  <Badge tone="danger">Crítica</Badge>
                ) : (
                  <Badge tone={template.active ? "achievement" : "neutral"}>
                    {template.active ? "Activa" : "Inactiva"}
                  </Badge>
                )}
              </>
            }
          />
        }
        footer={
          <EditorFooter
            onCancel={onCancel}
            action="canEdit"
            disabled={submitting}
            submitting={submitting}
          />
        }
      >
        {/* El `padding` del cuerpo lo pone ahora la región del `Modal`
          (`values.modalRegions.bodyPadding`, los mismos 20px 24px que tenía
          este `fieldset`): si se dejara acá quedaría duplicado. */}
        <fieldset
          disabled={!canEdit}
          style={{ border: "none", margin: 0, padding: 0 }}
        >
          <div style={{ display: "flex", gap: 14 }}>
            <Field label="Código">
              <input
                value={template.code}
                readOnly
                aria-readonly="true"
                style={{ ...inputStyle, color: tokens.text.tertiary }}
              />
            </Field>
            <Field label="Canal">
              <input
                value={CHANNEL_LABEL[template.channel]}
                readOnly
                aria-readonly="true"
                style={{ ...inputStyle, color: tokens.text.tertiary }}
              />
            </Field>
          </div>

          {/* No se usa `LabeledField` (siempre envuelve en <label>): un
            <label> con más de un control interactivo le asigna su texto
            como nombre accesible solo al primer descendiente enfocable, lo
            que rompía el nombre accesible de cada etiqueta insertable. */}
          <div style={{ marginBottom: 16 }}>
            <div className="label" style={{ marginBottom: 7 }}>
              Parámetros
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {template.parameters.length === 0 ? (
                <span
                  style={{
                    font: `400 12px ${tokens.font.ui}`,
                    color: tokens.text.tertiary,
                  }}
                >
                  Esta plantilla no tiene parámetros.
                </span>
              ) : (
                template.parameters.map((parameter) => (
                  <button
                    key={parameter}
                    type="button"
                    className="chip"
                    disabled={!canEdit}
                    onClick={() => insertParameter(parameter)}
                    title={`Insertar {${parameter}} en el cuerpo`}
                  >
                    {`{${parameter}}`}
                  </button>
                ))
              )}
            </div>
          </div>

          {isEmail ? (
            <Field
              label="Asunto"
              required={language === "es"}
              // Antes, un error de la API o del pre-chequeo en el asunto en
              // inglés (`subjectEn`) nunca se veía: este `error` solo leía
              // `subjectEs`, cualquiera fuera la pestaña activa.
              error={
                language === "es"
                  ? errors.subjectEs?.message
                  : errors.subjectEn?.message
              }
            >
              <input
                {...register(language === "es" ? "subjectEs" : "subjectEn")}
                placeholder={
                  language === "en"
                    ? "Sin traducir · se enviará el español"
                    : undefined
                }
                style={inputStyle}
              />
            </Field>
          ) : null}

          <Field
            label="Cuerpo"
            required
            hint="Párrafos, negrita, cursiva, enlaces y listas. Sin HTML libre."
            // Mismo fix que "Asunto": el parámetro faltante en inglés
            // (`findMissingParameterClient`) marca `bodyEn`, que antes no
            // tenía dónde mostrarse en la pestaña EN.
            error={
              language === "es"
                ? errors.bodyEs?.message
                : errors.bodyEn?.message
            }
          >
            <RichTextEditor
              key={language}
              ref={bodyEditorRef}
              value={activeBody}
              onChange={(html) =>
                setValue(language === "es" ? "bodyEs" : "bodyEn", html, {
                  shouldDirty: true,
                })
              }
              ariaLabel={`Cuerpo en ${language === "es" ? "español" : "inglés"}`}
            />
          </Field>

          {saveError ? (
            <div
              role="alert"
              style={{
                font: `500 12px ${tokens.font.ui}`,
                color: tokens.danger.base,
                marginBottom: 16,
              }}
            >
              {saveError}
            </div>
          ) : null}

          {testSent ? (
            <InlineNote>
              {`Se envió una prueba a tu correo (${testSent})`}
            </InlineNote>
          ) : null}

          <div style={{ display: "flex", gap: 10 }}>
            <RowIconAction
              label="Previsualizar"
              icon="eye"
              disabled={mutations.preview.isPending}
              onClick={openPreview}
            />
            <RowIconAction
              label="Enviar prueba"
              icon="send"
              disabled={mutations.sendTest.isPending}
              onClick={sendTest}
            />
            <RowIconAction
              label="Desactivar"
              icon="deactivate"
              tone="danger"
              disabled={template.isCritical || !canEdit || !template.active}
              {...(template.isCritical
                ? {
                    description:
                      "Las plantillas de acceso no se pueden desactivar",
                  }
                : {})}
              onClick={onDeactivate}
            />
          </div>
        </fieldset>
      </EditorModal>

      <TemplatePreviewDialog
        open={previewOpen}
        preview={mutations.preview.data}
        bilingual={bilingual}
        onClose={() => setPreviewOpen(false)}
      />
    </>
  );
}
